import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Loader2 } from 'lucide-react';
import { useInstance, useInstanceId } from '../contexts/InstanceContext';
import API_ENDPOINTS from '../config/api';
import { getDeviceId } from '../utils/deviceId';
import { SCENARIO_COLORS } from '../config/brandColors';
import MainViewHeader from '../components/MainViewHeader';
import { ViewModeSelector, ViewModeButton } from '../components/ViewModeSelector';

// Lenses components
import LensStartView from '../components/Lenses/LensStartView';
import PivotPointCurationView from '../components/Lenses/PivotPointCurationView';
import LensGridView from '../components/Lenses/LensGridView';
import LensDetailView from '../components/Lenses/LensDetailView';
import LensItemDetail from '../components/Lenses/LensItemDetail';
import LensDashboard from '../components/Lenses/LensDashboard';
import PivotSummaryView from '../components/Lenses/PivotSummaryView';
import LensItemModal from '../components/Lenses/LensItemModal';
import { saveLensItem } from '../components/Lenses/lensItemSave';
import AILensItemsModal from '../components/Lenses/AILensItemsModal';
import PivotPointEditModal from '../components/Lenses/PivotPointEditModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useTranslation } from '../hooks/useTranslation.jsx';
import { useAiUnread } from '../contexts/AiUnreadContext';
import { hasLensItemAiAnalysis } from '../utils/aiAnalysisPresence';
import useContentRefresh from '../hooks/useContentRefresh';
import useDeepLinkedPivotSummary from '../hooks/useDeepLinkedPivotSummary';
import { useFormCheckState } from '../hooks/useFormCheckUpdates';
import { FORM_CHECK_STAGES } from '../utils/formCheckDisplay';
import { isLowGfx } from '../utils/lowGfx';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
  overflow: hidden;
`;

const ContentArea = styled.div`
  padding-top: 64px; /* Header height */
  padding-right: 80px; /* Clear of right drawer tabs (chat rail) */
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ViewContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const LoadingContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
`;

const ErrorContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
  padding: 40px;
  text-align: center;
`;

const ErrorText = styled.span`
  color: #E94B6E;
  font-size: 0.85rem;
`;

// View states for the lenses flow
const VIEWS = {
  START: 'start',
  PIVOT_CURATION: 'pivot_curation',
  LENS_GRID: 'lens_grid',
  LENS_DETAIL: 'lens_detail',
  ITEM_DETAIL: 'item_detail',
  PIVOT_SUMMARY: 'pivot_summary',
  PIVOT_DETAIL_SUMMARY: 'pivot_detail_summary',
};

/**
 * LensesScreen - Day 2 analytical lenses orchestrator.
 * State-driven single route with views: start → pivot curation → lens grid → lens detail → item detail.
 * Follows PivotPointsScreen pattern (thin screen wiring Storybook components).
 */
export default function LensesScreen() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentInstance } = useInstance();
  const instanceId = useInstanceId(searchParams);

  // View navigation — restore from URL param
  const initialView = searchParams.get('view') || VIEWS.START;
  const [view, setView] = useState(
    Object.values(VIEWS).includes(initialView) ? initialView : VIEWS.START
  );

  // Data
  const [allPivotPoints, setAllPivotPoints] = useState([]);
  const [pivotPoints, setPivotPoints] = useState([]);
  const [pivotEdits, setPivotEdits] = useState({});
  const [lenses, setLenses] = useState([]);
  const [lensItems, setLensItems] = useState([]);
  const [itemCounts, setItemCounts] = useState({});
  const [pivotItemCounts, setPivotItemCounts] = useState({}); // { pivotId: { obstacles, opportunities } }
  const [pathwayNodes, setPathwayNodes] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [allDashboardItems, setAllDashboardItems] = useState([]);
  const [pivotSummaryData, setPivotSummaryData] = useState(null);
  const [pivotSummaryLoading, setPivotSummaryLoading] = useState(false);
  const [pivotSummaryError, setPivotSummaryError] = useState(null);
  const [pivotClusterData, setPivotClusterData] = useState(null);
  const [pivotClusterLoading, setPivotClusterLoading] = useState(false);
  const [pivotClusterError, setPivotClusterError] = useState(null);
  const [pivotReportLoading, setPivotReportLoading] = useState(false);
  const [enrichingItemIds, setEnrichingItemIds] = useState(new Set());
  const formCheck = useFormCheckState({
    instanceId,
    stage: FORM_CHECK_STAGES.LENS_ITEM,
    setRecords: setLensItems,
  });

  // Selection — restore from URL params
  const [selectedPivotId, setSelectedPivotId] = useState(searchParams.get('pivot') || null);
  const [selectedLensId, setSelectedLensId] = useState(searchParams.get('lens') || null);
  const selectedLens = lenses.find(l => l.id === selectedLensId) || null;
  const [selectedItem, setSelectedItem] = useState(null);
  const selectedItemRef = useRef(null);
  const pivotSummaryReturnRef = useRef(VIEWS.LENS_GRID);
  const { markUnread, markRead } = useAiUnread();

  useEffect(() => {
    selectedItemRef.current = selectedItem;
  }, [selectedItem]);

  // Modals
  const [modalState, setModalState] = useState(null); // { type, pivotPoint } or null
  const [aiModal, setAiModal] = useState(null); // { type, suggestions, loading, error } or null
  const [pivotEditModal, setPivotEditModal] = useState(null); // { mode, pivotPoint?, currentTitle? } or null
  const [deleteConfirm, setDeleteConfirm] = useState(null); // pivotPoint to confirm deletion

  // Loading/error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isLocked = !!currentInstance?.settings?.locked;

  // Sync view + pivot to URL params for reload resilience
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    let changed = false;

    if (view === VIEWS.START) {
      if (newParams.has('view')) { newParams.delete('view'); changed = true; }
    } else if (newParams.get('view') !== view) {
      newParams.set('view', view); changed = true;
    }

    if (selectedPivotId) {
      if (newParams.get('pivot') !== selectedPivotId) { newParams.set('pivot', selectedPivotId); changed = true; }
    } else {
      if (newParams.has('pivot')) { newParams.delete('pivot'); changed = true; }
    }

    if (selectedLensId) {
      if (newParams.get('lens') !== selectedLensId) { newParams.set('lens', selectedLensId); changed = true; }
    } else {
      if (newParams.has('lens')) { newParams.delete('lens'); changed = true; }
    }

    if (changed) setSearchParams(newParams, { replace: true });
  }, [view, selectedPivotId, selectedLensId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Instance sync handled by useInstanceId hook

  // ─── Data Fetching ──────────────────────────────────────────────────

  const fetchPivotPoints = useCallback(async () => {
    if (!instanceId) return;
    try {
      // Also fetch all lens_items for this instance — any pivot they reference
      // must stay visible even if it isn't in the curated `selectedPivotIds`,
      // otherwise lens-curation work silently disappears on this screen.
      // (Same bifurcation pattern as SolutionsScreen — see
      //  wiki/pages/pivot-ids-have-two-forms.md and commit 62bb359.)
      const [ppRes, instRes, liRes] = await Promise.all([
        fetch(API_ENDPOINTS.RAG_PIVOT_POINTS(instanceId)),
        fetch(`${API_ENDPOINTS.API_ORIGIN()}/api/instances/${instanceId}`),
        fetch(API_ENDPOINTS.LENS_ITEMS(instanceId)),
      ]);
      if (ppRes.ok) {
        const data = await ppRes.json();
        const all = data.pivotPoints || data || [];
        setAllPivotPoints(all);
        let instData = {};
        try { instData = await instRes.json(); } catch { /* ignore parse error */ }
        if (!searchParams.get('pivot') && instData.metadata?.selectedPivotId) {
          setSelectedPivotId(instData.metadata.selectedPivotId);
        }
        let lensItemsForAnchor = [];
        try { lensItemsForAnchor = liRes.ok ? await liRes.json() : []; } catch { /* */ }
        const anchoredPivotKeys = new Set(
          (Array.isArray(lensItemsForAnchor) ? lensItemsForAnchor : [])
            .map(li => li.pivot_point_id)
            .filter(Boolean)
        );
        const selectedIds = instData.metadata?.selectedPivotIds;
        const curationSet = selectedIds?.length > 0 ? new Set(selectedIds) : null;
        setPivotPoints(all.filter(pp => {
          const isCurated = curationSet
            ? (curationSet.has(pp.id) || curationSet.has(pp.clusterKey))
            : pp.source === 'manual';
          const hasLensItems = anchoredPivotKeys.has(pp.id) || anchoredPivotKeys.has(pp.clusterKey);
          return isCurated || hasLensItems;
        }));
      }
    } catch (err) {
      console.error('Error fetching pivot points:', err);
    }
  }, [instanceId, searchParams]);

  const fetchPivotEdits = useCallback(async () => {
    if (!instanceId) return;
    try {
      const response = await fetch(API_ENDPOINTS.PIVOT_POINT_EDITS(instanceId));
      if (response.ok) {
        const data = await response.json();
        setPivotEdits(data);
      }
    } catch (err) {
      console.error('Error fetching pivot edits:', err);
    }
  }, [instanceId]);

  const fetchLenses = useCallback(async () => {
    if (!instanceId) return;
    try {
      const response = await fetch(API_ENDPOINTS.LENSES(instanceId));
      if (!response.ok) return;
      let data = await response.json();

      // A new instance has no lenses yet: the backend creates the six standard
      // ones (lenses.json in @kuutio/workshop-modules) and returns them.
      if (data.length === 0) {
        const ensured = await fetch(API_ENDPOINTS.LENSES_ENSURE(instanceId), { method: 'POST' });
        if (ensured.ok) data = (await ensured.json()).lenses;
      }

      setLenses(data);
    } catch (err) {
      console.error('Error fetching lenses:', err);
    }
  }, [instanceId]);

  const fetchItemCounts = useCallback(async (pivotId) => {
    if (!instanceId) return;
    try {
      // Counts per lens, filtered to selected pivot
      let url = API_ENDPOINTS.LENS_ITEM_COUNTS(instanceId);
      if (pivotId) url += `?pivotPointId=${pivotId}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setItemCounts(data);
      }

      // Counts per pivot (for curation cards)
      const pivotRes = await fetch(`${API_ENDPOINTS.LENS_ITEM_COUNTS(instanceId)}?groupBy=pivot`);
      if (pivotRes.ok) {
        const pivotData = await pivotRes.json();
        setPivotItemCounts(pivotData);
      }
    } catch (err) {
      console.error('Error fetching item counts:', err);
    }
  }, [instanceId]);

  const fetchPathwayData = useCallback(async () => {
    if (!instanceId) return;
    try {
      const socketUrl = API_ENDPOINTS.API_ORIGIN();
      const response = await fetch(`${socketUrl}/api/pathways?kid=${instanceId}`);
      if (!response.ok) return;
      const data = await response.json();
      const pathways = data.pathways || data || [];

      // Build scenario list from scenario roots
      const scenarioRoots = pathways.filter(p => p.type === 'scenario_root');
      const scenarioIcons = ['🏛️', '🌍', '💻', '👥'];
      const scenarioGroups = new Map();
      scenarioRoots.forEach(root => {
        const mainName = root.text.includes(':') ? root.text.split(':')[0].trim() : root.text;
        if (!scenarioGroups.has(mainName)) {
          scenarioGroups.set(mainName, { id: root.scenarioId, roots: [] });
        }
        scenarioGroups.get(mainName).roots.push(root);
      });

      const builtScenarios = Array.from(scenarioGroups.entries()).map(([mainName, group], idx) => ({
        id: group.id,
        title: mainName,
        color: SCENARIO_COLORS[mainName] || ['#FFD93D', '#F5C6D6', '#2D9D78', '#2E7BC4'][idx % 4],
        icon: scenarioIcons[idx % 4],
      }));
      setScenarios(builtScenarios);

      // Build node list for branch view (submissions = depth > 0 non-root nodes)
      const nodeList = pathways
        .filter(p => p.type !== 'scenario_root' && p.type !== 'scenario_variable')
        .map(p => ({
          id: p.id,
          text: p.text,
          scenarioId: p.scenarioId,
          parentId: p.parentId,
          depth: p.depth,
          type: p.type,
        }));
      setPathwayNodes(nodeList);
    } catch (err) {
      console.error('Error fetching pathway data:', err);
    }
  }, [instanceId]);

  const fetchLensItems = useCallback(async (lensId, pivotId) => {
    if (!instanceId) return;
    try {
      let url = `${API_ENDPOINTS.LENS_ITEMS(instanceId)}?lensId=${lensId}`;
      if (pivotId) url += `&pivotPointId=${pivotId}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setLensItems(data);
      }
    } catch (err) {
      console.error('Error fetching lens items:', err);
    }
  }, [instanceId]);

  // Initial data load — pass selectedPivotId so counts match the restored selection
  useEffect(() => {
    if (!instanceId) return;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchPivotPoints(),
      fetchPivotEdits(),
      fetchLenses(),
      fetchItemCounts(selectedPivotId || undefined),
      fetchPathwayData(),
    ])
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [instanceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch counts filtered to selected pivot when it changes (skip initial load)
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!initialLoadDone.current) { initialLoadDone.current = true; return; }
    if (instanceId && selectedPivotId) {
      fetchItemCounts(selectedPivotId);
    }
  }, [selectedPivotId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore lens items when selectedLensId is set from URL but items aren't loaded
  useEffect(() => {
    if (selectedLensId && lensItems.length === 0 && !loading) {
      fetchLensItems(selectedLensId, selectedPivotId);
    }
  }, [selectedLensId, lenses]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch all lens items when entering dashboard summary view
  useEffect(() => {
    if (view === VIEWS.PIVOT_SUMMARY && instanceId) {
      fetch(API_ENDPOINTS.LENS_ITEMS(instanceId))
        .then(r => r.ok ? r.json() : [])
        .then(data => setAllDashboardItems(data))
        .catch(err => console.error('[Dashboard] fetch error:', err));
    }
  }, [view, instanceId]);

  // Live refresh when other devices add/edit lens content (throttled ≥60s)
  const refreshLensContent = useCallback(() => {
    if (!instanceId) return;
    fetchItemCounts(selectedPivotId || undefined);
    if (selectedLensId) fetchLensItems(selectedLensId, selectedPivotId);
    if (view === VIEWS.PIVOT_SUMMARY) {
      fetch(API_ENDPOINTS.LENS_ITEMS(instanceId))
        .then(r => r.ok ? r.json() : [])
        .then(data => setAllDashboardItems(data))
        .catch(err => console.error('[Dashboard] refresh error:', err));
    }
  }, [instanceId, selectedPivotId, selectedLensId, view, fetchItemCounts, fetchLensItems]);

  useContentRefresh(instanceId, ['lens_items', 'pivot_edits'], refreshLensContent, {
    enabled: !isLocked,
  });

  // Fall back to lens_grid if lens_detail view but no lens resolved (e.g. missing URL param)
  useEffect(() => {
    if (!loading && view === VIEWS.LENS_DETAIL && lenses.length > 0 && !selectedLens) {
      setView(VIEWS.LENS_GRID);
    }
  }, [loading, view, lenses, selectedLens]);

  // ─── Scenario Colors ─────────────────────────────────────────────────

  const scenarioColors = useMemo(() => {
    const colors = {};
    Object.entries(SCENARIO_COLORS).forEach(([name, color]) => {
      colors[name] = color;
    });
    return colors;
  }, []);

  // ─── Selected Pivot Point (single selection for lenses) ────────────

  const selectedPivotTitle = useMemo(() => {
    if (!selectedPivotId) return '';
    const fullList = allPivotPoints.length > 0 ? allPivotPoints : pivotPoints;
    const pp = fullList.find(p => p.id === selectedPivotId || p.clusterKey === selectedPivotId);
    if (!pp) return '';
    return pivotEdits[pp.id]?.title_override || pp.title || pp.text || pp.representative_text || '';
  }, [selectedPivotId, pivotPoints, allPivotPoints, pivotEdits]);

  const visiblePivotPoints = useMemo(() => {
    if (!selectedPivotId) return [];
    const fullList = allPivotPoints.length > 0 ? allPivotPoints : pivotPoints;
    const selected = fullList.find(pp => pp.id === selectedPivotId || pp.clusterKey === selectedPivotId);
    if (!selected) return [];
    return [{ ...selected, title: selectedPivotTitle || selected.title, description: pivotEdits[selected.id]?.description_override || selected.description }];
  }, [pivotPoints, allPivotPoints, selectedPivotId, selectedPivotTitle, pivotEdits]);

  const selectedPivotPoint = useMemo(() => {
    const fullList = allPivotPoints.length > 0 ? allPivotPoints : pivotPoints;
    const pp = fullList.find(p => p.id === selectedPivotId || p.clusterKey === selectedPivotId);
    if (!pp) return null;
    return { ...pp, title: selectedPivotTitle || pp.title, description: pivotEdits[pp.id]?.description_override || pp.description };
  }, [pivotPoints, allPivotPoints, selectedPivotId, selectedPivotTitle, pivotEdits]);

  // ─── Actions ────────────────────────────────────────────────────────

  const handleStart = () => {
    setView(VIEWS.PIVOT_CURATION);
  };

  const handleEditPivotPoint = (pivotPoint) => {
    const currentTitle = pivotEdits[pivotPoint.id]?.title_override || pivotPoint.title;
    const currentDescription = pivotEdits[pivotPoint.id]?.description_override || pivotPoint.description || '';
    setPivotEditModal({ mode: 'edit', pivotPoint, currentTitle, currentDescription });
  };

  const handleSavePivotEdit = async ({ title, description }) => {
    const { mode, pivotPoint } = pivotEditModal;
    setPivotEditModal(null);

    if (mode === 'edit' && pivotPoint) {
      try {
        await fetch(API_ENDPOINTS.PIVOT_POINT_EDITS_UPSERT(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            instance_id: instanceId,
            pivot_point_id: pivotPoint.id,
            title_override: title,
            description_override: description || null,
          }),
        });
        await fetchPivotEdits();
      } catch (err) {
        console.error('Error saving pivot edit:', err);
      }
    } else if (mode === 'create') {
      try {
        await fetch(API_ENDPOINTS.RAG_PIVOT_POINT_CREATE(instanceId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title, description }),
        });
        await fetchPivotPoints();
      } catch (err) {
        console.error('Error creating pivot point:', err);
      }
    }
  };

  const handleAddPivotPoint = () => {
    setPivotEditModal({ mode: 'create' });
  };

  const handleDeletePivotPoint = (pivotPoint) => {
    setDeleteConfirm(pivotPoint);
  };

  const executeDeletePivotPoint = async () => {
    const pivotPoint = deleteConfirm;
    setDeleteConfirm(null);
    try {
      // Soft-delete lens items belonging to this pivot first
      await fetch(API_ENDPOINTS.LENS_ITEMS_SOFT_DELETE_BY_PIVOT(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instanceId, pivotPointId: pivotPoint.id }),
      });

      const response = await fetch(API_ENDPOINTS.RAG_PIVOT_POINT_DELETE(instanceId, pivotPoint.id), {
        method: 'DELETE',
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Delete failed');
      }
      setPivotPoints(prev => prev.filter(pp => pp.id !== pivotPoint.id));
      await fetchItemCounts(selectedPivotId);
    } catch (error) {
      console.error('Delete pivot point error:', error);
    }
  };

  const handleContinueToLenses = () => {
    setView(VIEWS.LENS_GRID);
  };

  const handleSelectLens = async (lens) => {
    setSelectedLensId(lens.id);
    await fetchLensItems(lens.id, selectedPivotId);
    setView(VIEWS.LENS_DETAIL);
  };

  const handleAddItem = (type) => {
    setModalState({ type });
  };

  const fetchAISuggestions = useCallback(async (type) => {
    const pivotPoint = visiblePivotPoints[0];
    if (!pivotPoint || !selectedLens) return;

    setAiModal({ type, suggestions: [], loading: true, error: null });

    const body = JSON.stringify({
      instanceId,
      instanceTitle: currentInstance?.title || '',
      lensId: selectedLens.id,
      lensName: selectedLens.name,
      pivotPointId: pivotPoint.id,
      pivotPointTitle: pivotEdits[pivotPoint.id]?.title_override || pivotPoint.title,
      pivotPointDescription: pivotEdits[pivotPoint.id]?.description_override || pivotPoint.description || '',
      type,
      existingItems: lensItems.filter(i => i.type === type),
    });

    // Low-graphics devices (Samsung touchscreens, older WebView) — skip
    // SSE streaming and use the non-streaming endpoint instead. The
    // ReadableStream / SSE pipeline breaks silently on those browsers,
    // showing the empty 'Ei ehdotuksia saatavilla' state even though
    // the backend returns valid suggestions.
    if (isLowGfx()) {
      try {
        const r = await fetch(API_ENDPOINTS.AI_SUGGEST_LENS_ITEMS(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        });
        if (!r.ok) throw new Error(t('lenses.requestFailed'));
        const data = await r.json();
        setAiModal(prev => ({
          ...prev,
          suggestions: data.suggestions || [],
          loading: false,
        }));
      } catch (err) {
        setAiModal(prev => ({ ...prev, loading: false, error: err.message }));
      }
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.AI_SUGGEST_LENS_ITEMS_STREAM(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });

      if (!response.ok) throw new Error(t('lenses.requestFailed'));

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const msg = JSON.parse(line.slice(6));
            if (msg.event === 'suggestion') {
              // Append each suggestion as it arrives
              setAiModal(prev => ({
                ...prev,
                suggestions: [...prev.suggestions, msg.suggestion],
                loading: msg.index < msg.total - 1, // still loading if more coming
              }));
            } else if (msg.event === 'done') {
              setAiModal(prev => ({ ...prev, loading: false }));
            } else if (msg.event === 'error') {
              throw new Error(msg.message);
            }
          } catch { /* skip malformed SSE lines */ }
        }
      }

      // Ensure loading is false when stream ends
      setAiModal(prev => ({ ...prev, loading: false }));
    } catch (err) {
      setAiModal(prev => ({ ...prev, loading: false, error: err.message }));
    }
  }, [instanceId, currentInstance, selectedLens, visiblePivotPoints, pivotEdits, lensItems, t]);

  const handleAIGenerate = (type) => {
    fetchAISuggestions(type);
  };

  const handleAIAccept = async (suggestion) => {
    try {
      await fetch(API_ENDPOINTS.LENS_ITEM_CREATE(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instance_id: instanceId,
          lens_id: selectedLens.id,
          pivot_point_id: visiblePivotPoints[0]?.id,
          type: suggestion.type,
          title: suggestion.title,
          description: suggestion.description,
          source: 'ai',
          created_by: getDeviceId(),
          ai_confidence: suggestion.scores?.tags?.length
            ? Math.min(1, suggestion.scores.tags.length / 3)
            : (suggestion.scores?.quality ? suggestion.scores.quality / 100 : null),
          rag_references: {
            references: suggestion.ragReferences || [],
            scores: suggestion.scores || null,
          },
        }),
      });
      // Refresh after each save so the list updates
      if (selectedLens) await fetchLensItems(selectedLens.id, selectedPivotId);
      await fetchItemCounts(selectedPivotId);
    } catch (err) {
      console.error('Error saving AI suggestion:', err);
    }
  };

  const handleSaveItem = async (itemData) => {
    // A refused save throws out of here: LensItemModal stays open and says why.
    const saved = await saveLensItem(itemData, {
      instance_id: instanceId,
      lens_id: selectedLens?.id,
      pivot_point_id: visiblePivotPoints[0]?.id,
      type: itemData.type,
      title: itemData.title,
      description: itemData.description,
      source: itemData.source || 'manual',
      created_by: getDeviceId(),
      ai_confidence: itemData.ai_confidence || null,
      rag_references: itemData.rag_references || null,
    });
    try {
      let createdItemId = itemData.id;
      if (itemData.id) formCheck.noteEdited(itemData.id);
      else createdItemId = formCheck.noteCreated(saved).id;
      if (createdItemId && hasLensItemAiAnalysis({
        rag_references: itemData.rag_references,
        ai_confidence: itemData.ai_confidence,
      })) {
        markRead('lens_item', createdItemId);
      }
      setModalState(null);
      // Refresh items and counts
      if (selectedLens) await fetchLensItems(selectedLens.id, selectedPivotId);
      await fetchItemCounts(selectedPivotId);

      // AI enrichment — runs in background
      // New items: generate description + scores
      // Edited items: re-score only (keep user's description)
      const isNewItem = !itemData.id;
      if (createdItemId && (!itemData.source || itemData.source === 'manual')) {
        setEnrichingItemIds(prev => new Set([...prev, createdItemId]));
        const pivotPoint = visiblePivotPoints[0];
        fetch(API_ENDPOINTS.AI_ENRICH_LENS_ITEM(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: itemData.title,
            description: itemData.description || '',
            type: itemData.type,
            lensName: selectedLens?.name || '',
            pivotPointTitle: pivotEdits[pivotPoint?.id]?.title_override || pivotPoint?.title || '',
            instanceTitle: currentInstance?.title || '',
            instanceId: instanceId || '',
          }),
        })
          .then(r => r.ok ? r.json() : null)
          .then(enrichment => {
            if (!enrichment) return;
            const updatePayload = {
              ai_confidence: enrichment.scores?.tags?.length
                ? Math.min(1, enrichment.scores.tags.length / 3)
                : (enrichment.scores?.quality ? enrichment.scores.quality / 100 : null),
              rag_references: { scores: enrichment.scores, references: enrichment.references || [] },
            };
            // Only set AI description for new items — edits keep user's text
            if (isNewItem) {
              updatePayload.description = enrichment.description || itemData.title;
              updatePayload.ai_generated = true;
            }
            fetch(API_ENDPOINTS.LENS_ITEM_UPDATE(createdItemId), {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(updatePayload),
            }).then(() => {
              if (selectedLens) fetchLensItems(selectedLens.id, selectedPivotId);
              if (selectedItemRef.current?.id !== createdItemId) {
                markUnread('lens_item', createdItemId);
              }
            });
          })
          .catch(err => console.error('AI enrichment failed (non-blocking):', err))
          .finally(() => {
            setEnrichingItemIds(prev => {
              const next = new Set(prev);
              next.delete(createdItemId);
              return next;
            });
          });
      }
    } catch (err) {
      console.error('Error saving lens item:', err);
    }
  };

  const handleDeleteItem = (item) => {
    setDeleteConfirm(item);
  };

  const executeDeleteItem = async () => {
    const item = deleteConfirm;
    setDeleteConfirm(null);
    try {
      await fetch(API_ENDPOINTS.LENS_ITEM_DELETE(item.id), { method: 'DELETE' });
      if (selectedLens) await fetchLensItems(selectedLens.id, selectedPivotId);
      await fetchItemCounts(selectedPivotId);
    } catch (err) {
      console.error('Error deleting item:', err);
    }
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  const handleItemEdit = (item) => {
    setModalState({ type: item.type, existingItem: item });
  };

  const handleBackFromDetail = () => {
    setSelectedItem(null);
  };

  const handleBackFromLensDetail = () => {
    setSelectedLensId(null);
    setLensItems([]);
    setView(VIEWS.LENS_GRID);
  };

  const handleShowSummary = async () => {
    if (instanceId) {
      try {
        const r = await fetch(API_ENDPOINTS.LENS_ITEMS(instanceId));
        if (r.ok) setAllDashboardItems(await r.json());
      } catch (err) {
        console.error('[Dashboard] fetch error:', err);
      }
    }
    setView(VIEWS.PIVOT_SUMMARY);
  };

  const handleShowPivotSummary = async (pivotIdOverride = null, returnView = VIEWS.LENS_GRID) => {
    const pivotId = pivotIdOverride || selectedPivotId;
    if (pivotIdOverride && pivotIdOverride !== selectedPivotId) {
      setSelectedPivotId(pivotIdOverride);
    }
    pivotSummaryReturnRef.current = returnView;

    if (!instanceId || !pivotId) {
      setPivotSummaryData(null);
      setPivotSummaryError(t('lenses.pivotSummaryNeedPivot'));
      setPivotClusterData(null);
      setPivotClusterError(t('lenses.pivotSummaryNeedPivot'));
      setView(VIEWS.PIVOT_DETAIL_SUMMARY);
      return;
    }

    setPivotSummaryLoading(true);
    setPivotSummaryError(null);
    setPivotSummaryData(null);
    setPivotClusterLoading(true);
    setPivotClusterError(null);
    setPivotClusterData(null);
    setView(VIEWS.PIVOT_DETAIL_SUMMARY);

    try {
      const fullList = allPivotPoints.length > 0 ? allPivotPoints : pivotPoints;
      const pp = fullList.find(p => p.id === pivotId || p.clusterKey === pivotId);
      const edit = pivotEdits[pivotId];
      const title = edit?.title_override || pp?.title || selectedPivotTitle || '';
      const baseUrl = API_ENDPOINTS.LENS_PIVOT_SUMMARY(instanceId, pivotId);
      const clusterBaseUrl = API_ENDPOINTS.LENS_PIVOT_CLUSTERS(instanceId, pivotId);
      const summaryUrl = title
        ? `${baseUrl}?title=${encodeURIComponent(title)}`
        : baseUrl;
      const clusterUrl = title
        ? `${clusterBaseUrl}?title=${encodeURIComponent(title)}`
        : clusterBaseUrl;
      const [response, clusterResponse] = await Promise.all([
        fetch(summaryUrl),
        fetch(clusterUrl),
      ]);

      if (!response.ok) {
        throw new Error(`Summary fetch failed (${response.status})`);
      }
      const data = await response.json();
      setPivotSummaryData(data);
      if (!clusterResponse.ok) {
        throw new Error(`Cluster fetch failed (${clusterResponse.status})`);
      }
      const clusterData = await clusterResponse.json();
      setPivotClusterData(clusterData);
    } catch (err) {
      console.error('[PivotSummary] fetch error:', err);
      const msg = err?.message || '';
      if (msg.includes('Cluster fetch failed')) {
        setPivotClusterError(t('lenses.clustersFetchFailed'));
      } else {
        setPivotSummaryError(t('lenses.pivotSummaryLoadFailed'));
      }
    } finally {
      setPivotSummaryLoading(false);
      setPivotClusterLoading(false);
    }
  };

  const handleDashboardPivotClick = (pivotGroup) => {
    if (!pivotGroup?.pivotId) return;
    handleShowPivotSummary(pivotGroup.pivotId, VIEWS.PIVOT_SUMMARY);
  };

  // Direct URL into one pivot's summary; the hook explains the contract
  useDeepLinkedPivotSummary(
    initialView === VIEWS.PIVOT_DETAIL_SUMMARY ? searchParams.get('pivot') : null,
    instanceId,
    (pivotId) => handleShowPivotSummary(pivotId, VIEWS.PIVOT_SUMMARY),
  );

  const handleGeneratePivotReport = async () => {
    if (!instanceId || !selectedPivotPoint?.id || pivotReportLoading) return;
    setPivotReportLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.RAG_PIVOT_POINT_REPORT(instanceId, selectedPivotPoint.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aiModel: 'sonnet' }),
      });
      if (!response.ok) throw new Error(`Report generation failed (${response.status})`);

      const result = await response.json();
      const nextReport = result.report || null;
      if (!nextReport) return;

      setPivotPoints(prev => prev.map(pp => (
        pp.id === selectedPivotPoint.id
          ? { ...pp, report: nextReport }
          : pp
      )));
    } catch (err) {
      console.error('[PivotSummary] report generation error:', err);
      setPivotSummaryError(t('lenses.pivotReportFailed'));
    } finally {
      setPivotReportLoading(false);
    }
  };

  const handleBackFromSummary = () => {
    setAllDashboardItems([]);
    setPivotClusterData(null);
    setPivotClusterError(null);
    setPivotClusterLoading(false);
    setView(VIEWS.LENS_GRID);
  };

  const handleBackFromPivotDetailSummary = () => {
    setPivotSummaryData(null);
    setPivotSummaryError(null);
    setPivotClusterData(null);
    setPivotClusterError(null);
    setPivotClusterLoading(false);
    setView(pivotSummaryReturnRef.current || VIEWS.LENS_GRID);
  };

  // ─── Render ──────────────────────────────────────────────────────────

  if (!instanceId) {
    return (
      <Container>
        <ErrorContainer>
          {t('lenses.noInstance')}
        </ErrorContainer>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
          {t('lenses.loadingData')}
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          {t('lenses.loadErrorTitle')}
          <ErrorText>{error}</ErrorText>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <MainViewHeader
        activeView="lenses"
        instanceTitle={currentInstance?.title || ''}
        centerContent={
          view !== VIEWS.START ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <ViewModeSelector>
                <ViewModeButton
                  $active={view === VIEWS.PIVOT_CURATION}
                  onClick={() => setView(VIEWS.PIVOT_CURATION)}
                >
                  {t('lenses.navPivot')}
                </ViewModeButton>
                <ViewModeButton
                  $active={
                    view === VIEWS.LENS_GRID
                    || view === VIEWS.PIVOT_DETAIL_SUMMARY
                  }
                  onClick={() => {
                    if (selectedPivotId) setView(VIEWS.LENS_GRID);
                    else setView(VIEWS.PIVOT_CURATION);
                  }}
                >
                  {t('lenses.navLenses')}
                </ViewModeButton>
                <ViewModeButton
                  $active={view === VIEWS.LENS_DETAIL || view === VIEWS.ITEM_DETAIL}
                  disabled={!selectedLens}
                  onClick={() => {
                    if (selectedLens) setView(VIEWS.LENS_DETAIL);
                  }}
                >
                  {t('lenses.navEM')}
                </ViewModeButton>
                <ViewModeButton
                  $active={view === VIEWS.PIVOT_SUMMARY}
                  onClick={handleShowSummary}
                >
                  {t('lenses.navOverview')}
                </ViewModeButton>
              </ViewModeSelector>
              {selectedPivotTitle && view !== VIEWS.PIVOT_CURATION && (
                <span
                  title={selectedPivotTitle}
                  style={{
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.75)',
                    padding: '4px 10px',
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                  }}
                >
                  {selectedPivotTitle}
                </span>
              )}
            </div>
          ) : null
        }
      />
      <ContentArea>
      <ViewContent>
      {view === VIEWS.START && (
        <LensStartView
          onStart={handleStart}
          pivotPointCount={pivotPoints.length}
        />
      )}

      {view === VIEWS.PIVOT_CURATION && (
        <PivotPointCurationView
          pivotPoints={pivotPoints}
          edits={pivotEdits}
          itemCounts={pivotItemCounts}
          selectedPivotId={selectedPivotId}
          instanceId={instanceId}
          onSelect={(pp) => {
            setSelectedPivotId(pp.id);
            handleContinueToLenses();
          }}
          onContinue={handleContinueToLenses}
          onBack={() => setView(VIEWS.START)}
        />
      )}

      {view === VIEWS.LENS_GRID && (
        <LensGridView
          lenses={lenses}
          itemCounts={itemCounts}
          pivotPointTitle={selectedPivotTitle}
          onSelectLens={handleSelectLens}
          onShowPivotSummary={selectedPivotId ? () => handleShowPivotSummary() : undefined}
          onBack={() => setView(VIEWS.PIVOT_CURATION)}
        />
      )}

      {view === VIEWS.LENS_DETAIL && selectedLens && (
        <LensDetailView
          lens={selectedLens}
          pivotPointTitle={selectedPivotTitle}
          pivotPoints={visiblePivotPoints}
          items={lensItems}
          enrichingItemIds={enrichingItemIds}
          pendingFormCheckIds={formCheck.pendingIds}
          dismissedFormCheckIds={formCheck.dismissedIds}
          onDismissFormCheck={formCheck.dismiss}
          onBack={handleBackFromLensDetail}
          onAddItem={isLocked ? undefined : handleAddItem}
          onAIGenerate={isLocked ? undefined : handleAIGenerate}
          onItemClick={handleItemClick}
          onItemEdit={isLocked ? undefined : handleItemEdit}
          onItemDelete={isLocked ? undefined : handleDeleteItem}
          showDescription={!!currentInstance?.settings?.lenses?.generateDescription}
        />
      )}

      {view === VIEWS.PIVOT_SUMMARY && (
        <LensDashboard
          items={allDashboardItems}
          lenses={lenses}
          pivotPoints={pivotPoints}
          pivotEdits={pivotEdits}
          onBack={handleBackFromSummary}
          onPivotNavigate={handleDashboardPivotClick}
          showDescription={!!currentInstance?.settings?.lenses?.generateDescription}
        />
      )}

      {view === VIEWS.PIVOT_DETAIL_SUMMARY && (
        <PivotSummaryView
          pivotPoint={selectedPivotPoint ? {
            ...selectedPivotPoint,
            title: selectedPivotTitle || selectedPivotPoint.title,
          } : null}
          instanceId={instanceId}
          pivotPointId={selectedPivotId}
          lenses={lenses}
          items={pivotSummaryData?.items || []}
          summaryText={pivotSummaryData?.summary || ''}
          lensCounts={pivotSummaryData?.lensCounts || {}}
          loading={pivotSummaryLoading}
          error={pivotSummaryError}
          clusterData={pivotClusterData}
          clusterLoading={pivotClusterLoading}
          clusterError={pivotClusterError}
          pivotReport={selectedPivotPoint?.report || null}
          reportLoading={pivotReportLoading}
          onGenerateReport={handleGeneratePivotReport}
          onBack={handleBackFromPivotDetailSummary}
          onItemClick={handleItemClick}
          showDescription={!!currentInstance?.settings?.lenses?.generateDescription}
        />
      )}

      {/* Item detail modal overlay */}
      {selectedItem && (
        <>
          <div
            onClick={handleBackFromDetail}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)', zIndex: 1100, cursor: 'pointer',
            }}
          />
          <div style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%', maxWidth: '640px', height: '85vh',
            borderRadius: '16px', overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            zIndex: 1101,
          }}>
            <LensItemDetail
              item={lensItems.find(i => i.id === selectedItem.id) || selectedItem}
              pivotPoint={pivotPoints.find(pp => pp.id === selectedItem.pivot_point_id)}
              scenario={selectedLens}
              relatedItems={lensItems.filter(i => i.id !== selectedItem.id && i.pivot_point_id === selectedItem.pivot_point_id)}
              onBack={handleBackFromDetail}
              onEdit={isLocked ? undefined : (item) => { setSelectedItem(null); handleItemEdit(item); }}
              currentDeviceId={getDeviceId()}
              showDescription={!!currentInstance?.settings?.lenses?.generateDescription}
              formCheckPending={formCheck.pendingIds.has(selectedItem.id)}
              formCheckDismissed={formCheck.dismissedIds.has(selectedItem.id)}
              onDismissFormCheck={() => formCheck.dismiss(selectedItem.id)}
            />
          </div>
        </>
      )}
      </ViewContent>

      {/* Pivot Point Edit/Create Modal */}
      {pivotEditModal && (
        <PivotPointEditModal
          mode={pivotEditModal.mode}
          pivotPoint={pivotEditModal.pivotPoint}
          currentTitle={pivotEditModal.currentTitle}
          currentDescription={pivotEditModal.currentDescription}
          instanceTitle={currentInstance?.title || ''}
          instanceId={instanceId}
          onSave={handleSavePivotEdit}
          onClose={() => setPivotEditModal(null)}
        />
      )}

      {/* Delete Confirmation (pivot points and lens items) */}
      {deleteConfirm && (
        <ConfirmDialog
          title={deleteConfirm.source ? t('lenses.deleteItemTitle') : t('lenses.deletePivotTitle')}
          message={t('lenses.deleteConfirm', { title: deleteConfirm.title })}
          confirmLabel={t('common.delete')}
          destructive
          onConfirm={deleteConfirm.lens_id ? executeDeleteItem : executeDeletePivotPoint}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}


      {/* Add/Edit Item Modal */}
      {modalState && (
        <LensItemModal
          type={modalState.type}
          existingItem={modalState.existingItem}
          lensName={selectedLens?.name}
          pivotPointTitle={selectedPivotTitle}
          instanceId={instanceId}
          onSave={handleSaveItem}
          onClose={() => setModalState(null)}
        />
      )}

      {/* AI Suggestions Modal */}
      {aiModal && (
        <AILensItemsModal
          type={aiModal.type}
          lensName={selectedLens?.name || ''}
          pivotPointTitle={selectedPivotTitle}
          suggestions={aiModal.suggestions}
          loading={aiModal.loading}
          error={aiModal.error}
          onAccept={handleAIAccept}
          onRegenerate={() => fetchAISuggestions(aiModal.type)}
          onClose={() => setAiModal(null)}
          showDescription={!!currentInstance?.settings?.lenses?.generateDescription}
        />
      )}
      </ContentArea>
    </Container>
  );
}
